import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorMessage, fetchCompanyIndicators, fetchCompanyProfile, type CompanyProfile } from './api';
import { rememberRequestedCompanyIndicators } from './companyReportAccess';
import CompanyHistory from './CompanyHistory';

interface CompanyProfilePanelProps {
  pageId: string;
  vatNumber: string;
}

const integer = new Intl.NumberFormat('it-IT');
const compactEuro = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  notation: 'compact',
  maximumFractionDigits: 1,
});
const fullEuro = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});
const date = new Intl.DateTimeFormat('it-IT', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function money(value: number | null): string {
  if (value == null) return 'n/d';
  return Math.abs(value) >= 100_000 ? compactEuro.format(value) : fullEuro.format(value);
}

function startYear(value: string | null): string {
  if (!value) return 'n/d';
  const match = value.match(/\d{4}/);
  return match?.[0] ?? value;
}

function RegistryIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 21h16M6 21V9l6-4 6 4v12M9 12h1m4 0h1M9 16h1m4 0h1"/><path d="M10 21v-2.5a2 2 0 0 1 4 0V21"/></svg>;
}

export default function CompanyProfilePanel({ pageId, vatNumber }: CompanyProfilePanelProps) {
  const queryClient = useQueryClient();
  const cacheKey = ['company-profile', pageId] as const;
  const [profile, setProfile] = useState<CompanyProfile | null>(
    () => queryClient.getQueryData<CompanyProfile>(cacheKey) ?? null,
  );
  const company = useMutation({
    mutationFn: (force: boolean) => fetchCompanyProfile(pageId, force),
    onSuccess: (result) => {
      setProfile(result);
      queryClient.setQueryData(cacheKey, result);
      queryClient.invalidateQueries({ queryKey: ['company-indicators', vatNumber] });
      rememberRequestedCompanyIndicators(pageId, vatNumber);
    },
  });
  const indicators = useQuery({
    queryKey: ['company-indicators', vatNumber],
    queryFn: () => fetchCompanyIndicators(vatNumber),
    enabled: Boolean(profile),
    staleTime: 30 * 60_000,
  });
  const loadingLabel = profile ? 'Aggiorno i dati…' : 'Recupero i dati…';

  return <section className="company-profile-panel" aria-labelledby={`company-profile-${pageId}`}>
    <header className="company-profile-head">
      <div className="company-profile-title">
        <span className="company-profile-icon"><RegistryIcon /></span>
        <div><h5 id={`company-profile-${pageId}`}>Dati societari</h5><p>Registro Imprese · P.IVA {vatNumber}</p></div>
      </div>
      <button type="button" className={profile ? 'btn-quiet' : 'btn-secondary'} disabled={company.isPending} onClick={() => company.mutate(Boolean(profile))}>
        {company.isPending ? loadingLabel : profile ? 'Aggiorna · ~€0,10' : 'Recupera · ~€0,10'}
      </button>
    </header>

    {!profile && !company.isPending && <p className="company-profile-intro">Anagrafica ufficiale, codice ATECO e numeri dell’ultimo bilancio depositato. La richiesta ha un costo indicativo di circa €0,10.</p>}
    {company.isPending && !profile && <div className="company-profile-loading" role="status"><span className="sr-only">Recupero dati societari</span><i/><i/><i/></div>}
    {company.isError && <div className="notice notice-error company-profile-error" role="alert"><p>{errorMessage(company.error)}</p><button type="button" className="btn-secondary" onClick={() => company.mutate(Boolean(profile))}>Riprova</button></div>}

    {profile && <>
      <div className="company-profile-identity">
        <div><span>Impresa</span><strong>{profile.companyName || 'Ragione sociale non disponibile'}</strong><small>{[profile.legalForm, profile.activityStatus].filter(Boolean).join(' · ') || 'Stato societario non disponibile'}</small></div>
        <dl>
          <div><dt>ATECO</dt><dd>{[profile.atecoCode, profile.atecoDescription].filter(Boolean).join(' · ') || 'n/d'}</dd></div>
          <div><dt>Costituita nel</dt><dd>{startYear(profile.startDate)}</dd></div>
          {profile.pec && <div><dt>PEC</dt><dd>{profile.pec}</dd></div>}
        </dl>
      </div>

      <div className="company-balance-head"><div><span>Ultimo bilancio depositato</span><strong>{profile.balanceYear ?? 'Anno non disponibile'}</strong></div><small>I valori mancanti non vengono stimati.</small></div>
      <dl className="company-financials">
        <div><dt>Fatturato</dt><dd>{money(profile.turnover)}</dd></div>
        <div><dt>Dipendenti</dt><dd>{profile.employees != null ? integer.format(profile.employees) : 'n/d'}</dd></div>
        <div className={profile.netWorth != null && profile.netWorth < 0 ? 'is-negative' : undefined}><dt>Patrimonio netto</dt><dd>{money(profile.netWorth)}</dd>{profile.netWorth != null && profile.netWorth < 0 && <small>Patrimonio netto negativo</small>}</div>
        <div><dt>Capitale sociale</dt><dd>{money(profile.shareCapital)}</dd></div>
      </dl>
      <CompanyHistory indicators={indicators.data} isLoading={indicators.isFetching} error={indicators.error} onRetry={() => indicators.refetch()}/>
      <footer className="company-profile-source"><span>{indicators.data?.fonte || 'Elaborazione Osservio su dati ufficiali Registro Imprese'}</span><span>Aggiornato {date.format(new Date(profile.fetchedAt))}</span></footer>
    </>}
  </section>;
}
